import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { ADD_BACKEND_AUTH } from '@app/api/auth.tokens';

const COOKIE_NAME = 'pwd_ref';
const NINETY_DAYS_SECONDS = 90 * 24 * 60 * 60;
const CODE_PATTERN = /^[a-z0-9-]{4,32}$/;
const CLAIMED_FLAG = 'pwd_ref_claimed';

export interface ClaimResult {
  attributed: boolean;
  partner_name: string | null;
}

/**
 * Il codice referral arriva o dal cookie scritto dal sito, o da ?ref= sull'app.
 * I due percorsi convergono sullo stesso cookie: da lì in poi ce n'è uno solo.
 *
 * Il `source` viaggia dentro il valore del cookie (`<code>.<source>`) perché
 * altrimenti si perderebbe alla convergenza, e non sapremmo più quale dei due
 * link porta davvero gente.
 */
@Injectable({ providedIn: 'root' })
export class ReferralService {
  constructor(private http: HttpClient) {}

  captureFromUrl(search: string = window.location.search): void {
    const code = (new URLSearchParams(search).get('ref') || '').trim().toLowerCase();
    if (!CODE_PATTERN.test(code)) return;
    this.writeCookie(`${code}.app`);
  }

  claimPending(): Observable<ClaimResult | null> {
    if (this.safeSessionGet(CLAIMED_FLAG)) return of(null);

    const raw = this.readCookie();
    if (!raw) return of(null);

    const separator = raw.lastIndexOf('.');
    const code = separator === -1 ? raw : raw.slice(0, separator);
    const source = separator === -1 ? 'site' : raw.slice(separator + 1);
    if (!CODE_PATTERN.test(code)) return of(null);

    const context = new HttpContext().set(ADD_BACKEND_AUTH, true);
    return this.http
      .post<ClaimResult>(
        `${environment.backendDomain}/api/referral/claim/`,
        { code, source },
        { context },
      )
      .pipe(tap(() => this.safeSessionSet(CLAIMED_FLAG, '1')));
  }

  private writeCookie(value: string): void {
    const domain = window.location.hostname.endsWith('prowodo.com')
      ? '; Domain=.prowodo.com'
      : '';
    // Secure solo su https: Karma gira gli spec su http://localhost, dove un
    // cookie Secure non si scriverebbe e uno spec ne verifica il valore.
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    try {
      document.cookie =
        `${COOKIE_NAME}=${value}; Path=/; Max-Age=${NINETY_DAYS_SECONDS}; SameSite=Lax${domain}${secure}`;
    } catch {
      /* Cookie scrittura negata dall'utente (blocco cookie/site data): non c'è
       * nulla da attribuire, si prosegue senza. */
    }
  }

  private readCookie(): string | null {
    try {
      const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      // document.cookie negato dal browser, oppure valore non decodificabile
      // (es. un `%` isolato -> URIError). In entrambi i casi non c'è un
      // codice utilizzabile.
      return null;
    }
  }

  /** sessionStorage.getItem lancia SecurityError se l'utente ha bloccato
   * cookie/site data (Chrome, Firefox): mai lasciarlo uscire sincrono dal
   * subscriber, vedi base-authenticated.service.ts e seen-nudges.service.ts. */
  private safeSessionGet(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private safeSessionSet(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  }
}
